import { useCallback } from "react";
import { ApolloError } from "@apollo/client";
import { toast } from "@jsq/ds/core";
import { addError } from "@jsq/observability";
import { formatError } from "#common/hooks/useReportError";
import {
  useCreatePayeeEntityMutation,
  usePatchPayeeEntityMutation,
} from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import { PayeeWorkflowModes } from "../../../../Context/constants";
import {
  useBasePayeeReducerContext,
  usePayeeWorkflowContext,
} from "../../../../Context/PayeeContext";
import { generatePayeePatchDiff } from "./utils";
import type {
  CreatePayeeEntityMutation,
  PatchPayeeEntityMutation,
} from "#graphql/supergraphTypes";

type CreatedEntityResult =
  | CreatePayeeEntityMutation["createPayeeEntity"]
  | PatchPayeeEntityMutation["patchPayeeEntity"]
  | null;

export interface UseSaveEntityPayeeHandlerOptions {
  onSuccess?: (createdPayee?: CreatedEntityResult) => void;
  onError?: (error: Error) => void;
}

export interface UseSaveEntityPayeeHandlerReturn {
  savePayee: () => Promise<void>;
  loading: boolean;
  error: ApolloError | undefined;
}

/**
 * Hook that abstracts entity payee save logic, returning either create or patch mutations
 * based on workflow mode props. Handles success/error handling, and redirect behavior.
 */
export const useSaveEntityPayeeHandler = (
  options: UseSaveEntityPayeeHandlerOptions = {},
): UseSaveEntityPayeeHandlerReturn => {
  const { onSuccess, onError } = options;

  // Access payee data from context
  const payeeData = useBasePayeeReducerContext();
  const { arenaId, payeeName, paymentPref, supportingDocuments, payeeId } = payeeData;
  const { mode, originalPayeeData } = usePayeeWorkflowContext();

  const defaultOnSuccess = useCallback(() => {
    window.location.href = "/canopy/treasury/payees";
  }, []);

  const defaultOnError = useCallback((error: Error) => {
    const errorMessage = error instanceof Error ? error.message : "An error occurred";
    toast.error(errorMessage);
  }, []);

  const [createPayeeEntity, { loading: createLoading, error: createError }] =
    useCreatePayeeEntityMutation({
      onCompleted: (data: CreatePayeeEntityMutation) => {
        const createdPayee = data?.createPayeeEntity;
        onSuccess ? onSuccess(createdPayee) : defaultOnSuccess();
      },
      onError: (error: ApolloError) => {
        addError(formatError(error));
        onError ? onError(error) : defaultOnError(error);
      },
      context: clientContext(),
    });

  const [patchPayeeEntity, { loading: patchLoading, error: patchError }] =
    usePatchPayeeEntityMutation({
      onCompleted: (data: PatchPayeeEntityMutation) => {
        const updatedPayee = data?.patchPayeeEntity;
        onSuccess ? onSuccess(updatedPayee) : defaultOnSuccess();
      },
      onError: (error: ApolloError) => {
        addError(formatError(error));
        onError ? onError(error) : defaultOnError(error);
      },
      context: clientContext(),
    });

  const savePayee = useCallback(async () => {
    const paymentPrefId = paymentPref?.id;
    if (!arenaId) {
      const error = new Error("Missing required information");
      onError ? onError(error) : defaultOnError(error);
      return;
    }

    try {
      if (mode === PayeeWorkflowModes.EDIT) {
        if (!payeeId) {
          const errorMessage = "Cannot find payee to update";
          toast.error(errorMessage);
          return;
        }

        if (!originalPayeeData) {
          const errorMessage = "Cannot find original payee data for comparison";
          toast.error(errorMessage);
          return;
        }

        // Generate patch diff to only send changed fields
        const patchDiff = generatePayeePatchDiff(payeeData, originalPayeeData);

        if (!patchDiff) {
          // No changes detected, show success without making API call
          onSuccess ? onSuccess() : defaultOnSuccess();
          return;
        }

        await patchPayeeEntity({
          variables: {
            patchPayeeEntityInput: {
              payeeId: payeeId,
              ...patchDiff,
            },
          },
        });
      } else {
        if (!payeeName || !supportingDocuments || !paymentPrefId) {
          const error = new Error("Missing required information");
          onError ? onError(error) : defaultOnError(error);
          return;
        }

        await createPayeeEntity({
          variables: {
            createPayeeEntityInput: {
              arenaId: arenaId,
              name: payeeName,
              paymentPrefId: paymentPrefId,
              supportingDocuments: supportingDocuments.map((doc) => ({
                name: doc.name,
                size: doc.size,
                s3ObjectKey: doc.s3ObjectKey,
              })),
            },
          },
        });
      }
    } catch (error) {
      // Apollo errors are reported by the mutation's onError callback above;
      // only report unexpected non-Apollo errors here to avoid double-fire.
      if (!(error instanceof ApolloError)) {
        addError(formatError(error));
      }
      toast.error("Unexpected error while submitting.");
      return;
    }
  }, [
    arenaId,
    payeeName,
    paymentPref,
    supportingDocuments,
    payeeId,
    mode,
    createPayeeEntity,
    patchPayeeEntity,
    onSuccess,
    onError,
    defaultOnSuccess,
    defaultOnError,
  ]);

  return {
    savePayee,
    loading: createLoading || patchLoading,
    error: createError || patchError,
  };
};
